import React from "react";
import { Box } from "@mui/material";
import { MdCheckCircle } from "react-icons/md";

const raleway = { fontFamily: "'Geist', sans-serif" };
const geistMono = { fontFamily: "'Geist Mono', monospace" };
const instrumentSerif = { fontFamily: "'Instrument Serif', serif" };

const TIERS = [
  {
    name: "Starter",
    amount: "₦50,000",
    description:
      "For traders and petty business owners starting or managing daily sales.",
    features: ["3% monthly interest", "Flexible access to funds"],
    highlight: false,
  },
  {
    name: "Growth",
    amount: "₦200,000",
    description:
      "For women growing their businesses and needing more working capital.",
    features: ["3% monthly interest", "Supports restocking and expansion."],
    highlight: true,
  },
  {
    name: "Premium",
    amount: "₦500,000",
    description: "For established entrepreneurs ready to scale operations.",
    features: ["3% monthly interest", "Designed for bigger business needs"],
    highlight: false,
  },
];

const TierCard = ({ name, amount, description, features, highlight }) => {
  const textColor = highlight ? "#FFFFFF" : "#132A22";
  const mutedColor = highlight ? "rgba(255,255,255,0.85)" : "#3F5445";
  const dividerColor = highlight ? "rgba(255,255,255,0.18)" : "#E5E5E5";
  const buttonBorder = highlight ? "rgba(255,255,255,0.4)" : "#D1D5D2";
  const nameColor = highlight ? "#86C7A8" : "#3F5445";

  return (
    <Box
      sx={{
        flex: "1 1 0",
        minWidth: 0,
        background: highlight ? "#132A22" : "#FFFFFF",
        borderRadius: "16px",
        padding: "clamp(24px, 2.5vw, 36px)",
        display: "flex",
        flexDirection: "column",
        gap: "20px",
        boxShadow: highlight
          ? "0px 24px 48px -12px rgba(3, 34, 17, 0.25)"
          : "0px 12px 32px -12px rgba(10, 13, 18, 0.08)",
        "@media (max-width: 767px)": { width: "100%", flex: "0 0 auto" },
      }}
    >
      <p
        style={{
          ...raleway,
          fontWeight: 600,
          fontSize: "13px",
          letterSpacing: "0.12em",
          textTransform: "uppercase",
          color: nameColor,
          margin: 0,
          lineHeight: 1,
        }}
      >
        {name}
      </p>

      <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
        <p
          style={{
            ...raleway,
            fontWeight: 400,
            fontSize: "14px",
            color: mutedColor,
            margin: 0,
            lineHeight: 1,
          }}
        >
          Up to
        </p>
        <p
          style={{
            ...instrumentSerif,
            fontWeight: 400,
            fontSize: "clamp(36px, 3.89vw, 56px)",
            lineHeight: 1,
            color: textColor,
            margin: 0,
            letterSpacing: "-0.01em",
          }}
        >
          {amount}
        </p>
      </div>

      <p
        style={{
          ...raleway,
          fontWeight: 400,
          fontSize: "16px",
          lineHeight: 1.6,
          color: mutedColor,
          margin: 0,
        }}
      >
        {description}
      </p>

      <div style={{ height: "1px", background: dividerColor }} />

      <ul
        style={{
          listStyle: "none",
          margin: 0,
          padding: 0,
          display: "flex",
          flexDirection: "column",
          gap: "12px",
        }}
      >
        {features.map((feature) => (
          <li
            key={feature}
            style={{
              ...raleway,
              display: "flex",
              alignItems: "center",
              gap: "10px",
              fontWeight: 500,
              fontSize: "15px",
              lineHeight: 1.4,
              color: textColor,
            }}
          >
            <MdCheckCircle
              size={18}
              color="#86C7A8"
              style={{ flexShrink: 0 }}
            />
            {feature}
          </li>
        ))}
      </ul>

      <a
        href="https://zikoramfb.qoreonline.com/onboarding/type"
        target="_blank"
        rel="noopener noreferrer"
        style={{
          ...raleway,
          fontWeight: 600,
          fontSize: "15px",
          color: textColor,
          background: "transparent",
          border: `1px solid ${buttonBorder}`,
          borderRadius: "8px",
          padding: "12px 20px",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          alignSelf: "flex-start",
          cursor: "pointer",
          marginTop: "10px",
          textDecoration: "none",
        }}
      >
        Apply Now
      </a>
    </Box>
  );
};

const LoanOptions = () => {
  return (
    <Box
      sx={{
        background: "transparent",
        boxSizing: "border-box",
        padding: "clamp(48px, 6.94vw, 100px) clamp(20px, 5.56vw, 80px)",
      }}
    >
      <div style={{ maxWidth: "1280px", margin: "0 auto" }}>
        {/* Section header */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
            gap: "clamp(12px, 1.39vw, 20px)",
            marginBottom: "clamp(40px, 4.17vw, 60px)",
          }}
        >
          {/* Label */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <span
              style={{
                width: "8px",
                height: "8px",
                borderRadius: "50%",
                backgroundColor: "#295B4A",
                flexShrink: 0,
                display: "inline-block",
              }}
            />
            <span
              style={{
                ...geistMono,
                fontWeight: 500,
                fontSize: "12px",
                lineHeight: 1,
                letterSpacing: "0.1em",
                color: "#295B4A",
                textTransform: "uppercase",
              }}
            >
              Pricing
            </span>
          </div>

          <h2
            style={{
              ...instrumentSerif,
              fontWeight: 400,
              fontSize: "clamp(28px, 4.17vw, 60px)",
              lineHeight: 1.1,
              letterSpacing: "-0.01em",
              color: "#132A22",
              margin: 0,
              maxWidth: "min(720px, 100%)",
            }}
          >
            Choose the option that fits your business today.
          </h2>

          <p
            style={{
              ...raleway,
              fontWeight: 400,
              fontSize: "clamp(14px, 1.25vw, 18px)",
              lineHeight: 1.6,
              color: "#3F5445",
              margin: 0,
              maxWidth: "min(560px, 100%)",
            }}
          >
            Three tiers. The same simple rate. Step up as your business grows.
          </p>
        </div>

        <div
          className="flex sm:flex-col"
          style={{ gap: "20px", alignItems: "stretch" }}
        >
          {TIERS.map((tier) => (
            <TierCard key={tier.name} {...tier} />
          ))}
        </div>
      </div>
    </Box>
  );
};

export default LoanOptions;
